import {
  HStack,
  VStack,
  Box,
  Image,
  Text,
  StackProps,
  Flex,
  Badge,
  Heading,
  useDisclosure,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  useMediaQuery,
  AccordionPanel,
  Avatar,
  AvatarGroup,
  Link
} from '@chakra-ui/react'

import AlertModal from 'components/AlertModal'
import { SecondaryButton } from 'components/Buttons'
import { Agent, Enum_Agent_Status, Maybe, useGetMyUserQuery, Property } from 'generated/graphql'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import React from 'react'
import { VscTrash } from 'react-icons/vsc'

import { images, theme } from 'theme'
import { formatPropertyTitle, getColorPerStatus, amountFormatter } from 'utils'
import { useGeolocation } from 'hooks'

type AgentCardProps = {
  agent: Maybe<Agent>
  onConfirmArchive?: (id?: string) => Promise<void>
  loading?: boolean
  isMobile?: boolean
  isFocused?: boolean
} & StackProps

const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  onConfirmArchive,
  loading,
  isMobile,
  isFocused,
  ...rest
}) => {
  const { currency } = useGeolocation()
  const { isOpen: isArchiveOpen, onClose: onArchiveClose, onOpen: onArchiveOpen } = useDisclosure()
  const [isTabletOrMobile] = useMediaQuery('(max-width: 50em)')
  const isActive = agent?.status === Enum_Agent_Status.Active
  const { data: me } = useGetMyUserQuery()
  const { t, i18n } = useTranslation()
  const translator = i18n as any
  const lang = translator.language as string

  return (
    <>
      <AlertModal
        activatorStatus={isActive ? 'deactivate' : 'activate'}
        onConfirm={async () => {
          await onConfirmArchive?.(agent?.id)
          onArchiveClose()
        }}
        loading={loading}
        isOpen={isArchiveOpen}
        onClose={onArchiveClose}
        confirmButtonText={isActive ? 'Archive' : 'Unarchive'}
        title={isActive ? 'Archive' : 'Unarchive'}
      >
        <Heading fontSize="sm">
          Are you sure you want to {isActive ? 'archive' : 'unarchive'} this agent?
        </Heading>
        <Text fontSize="xs" mt={8}>
          {isActive
            ? 'This agent will no longer be able to login to the system. and all the properties will be removed from the agent and unpublished.'
            : 'This agent will be able to login to the system. and all the properties will be added to the agent.'}
        </Text>
      </AlertModal>
      <Flex
        pt={isMobile ? 0 : 4}
        cursor="pointer"
        direction={isMobile ? 'column' : 'row'}
        border="2px solid"
        rounded="lg"
        boxShadow={isMobile || !isFocused ? 'none' : theme.boxShadow2}
        borderColor={isFocused ? 'orange.100' : isMobile ? 'transparent' : 'gray.100'}
        bg={isFocused ? 'gray.50' : 'white'}
        key={agent?.id}
        px={isMobile ? 0 : isTabletOrMobile ? 6 : 8}
        py={4}
        width="100%"
        {...rest}
      >
        <Image
          src={
            agent?.photo?.formats?.thumbnail?.url ||
            me?.getMyUser?.agent?.agency?.logo?.url ||
            agent?.photo?.url ||
            images.icon
          }
          borderRadius="2xl"
          objectFit="cover"
          mr={4}
          mb={4}
          boxSize={12}
        />
        <HStack flex={1} mb={4} alignItems="flex-start" spacing={4}>
          <VStack spacing={0} flex={1} alignItems="flex-start">
            <HStack justifyContent="space-between" w="100%">
              <Text fontWeight="extrabold" fontSize="md">
                {agent?.name}
              </Text>
              <HStack>
                <Badge
                  colorScheme={getColorPerStatus(agent?.user?.role?.type || '')}
                  fontSize="x-small"
                  rounded="full"
                  textTransform="capitalize"
                  px={2}
                  py={1}
                >
                  {agent?.user?.role?.type}
                </Badge>
                <Badge
                  rounded="full"
                  px={2}
                  textTransform="capitalize"
                  py={1}
                  colorScheme={getColorPerStatus(agent?.status || '')}
                  fontSize="x-small"
                >
                  {agent?.status}
                </Badge>
              </HStack>
            </HStack>

            <Box>
              <Text
                color="gray.600"
                wordBreak="break-all"
                noOfLines={1}
                fontWeight="semibold"
                fontSize="xx-small"
              >
                {agent?.agent_number}
              </Text>
              <Text color="gray.600" wordBreak="break-all" noOfLines={1} fontSize="xs">
                {agent?.email}
              </Text>

              <Text color="orange.500" fontSize="sm" fontWeight="semibold">
                {agent?.phone_number}
              </Text>
            </Box>
            <Text color="gray.500" fontSize="x-small">
              Added {moment(agent?.created_at).calendar()}
            </Text>
            <Box>
              {onConfirmArchive && (
                <VStack alignItems="flex-start" my={4} spacing={4}>
                  <SecondaryButton
                    onClick={onArchiveOpen}
                    leftIcon={isActive ? <VscTrash /> : undefined}
                    color={isActive ? 'red.500' : 'green.500'}
                  >
                    {isActive ? 'Archive' : 'Unarchive'}
                  </SecondaryButton>{' '}
                </VStack>
              )}
            </Box>
            {onConfirmArchive && (
              <Accordion borderColor="transparent" w="100%" p={0} allowMultiple>
                <AccordionItem p={0}>
                  <AccordionButton _hover={{ bg: 'transparent' }} p={0}>
                    <HStack spacing={2} as="span" flex="1" textAlign="left">
                      <Text color="gray.700" fontWeight="extrabold">
                        {agent?.properties?.length || 0}
                      </Text>
                      <Text fontSize="sm" color="blue.500">
                        properties
                      </Text>
                    </HStack>
                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel px={0}>
                    {agent?.properties?.map((property) => (
                      <HStack
                        spacing={3}
                        py={2}
                        alignItems="flex-start"
                        borderBottom="1px solid"
                        borderColor="gray.50"
                        key={property?.id}
                      >
                        <AvatarGroup size="sm" max={1}>
                          {property?.photos?.map((image) => (
                            <Avatar key={image?.id} name={image?.name} src={image?.url} />
                          ))}
                        </AvatarGroup>
                        <VStack spacing={0} alignItems="flex-start">
                          <Text fontWeight="semibold" fontSize="xs">
                            {formatPropertyTitle(property as Property, t, lang)}
                          </Text>
                          <Text fontWeight="extrabold" fontSize="sm">
                            {amountFormatter(property?.price || 0, currency)}
                          </Text>
                          <Text fontSize="xs" color="gray.800">
                            {property?.street_address}
                          </Text>
                          <Text color="gray.500" noOfLines={2} lineHeight="1rem" fontSize="xs">
                            {property?.description}
                          </Text>
                          <Link
                            href={`/property/${property?.id}`}
                            isExternal
                            color="blue.500"
                            fontSize="xs"
                          >
                            <Text color="blue.500" fontSize="xs">
                              View property
                            </Text>
                          </Link>
                        </VStack>
                      </HStack>
                    ))}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}
          </VStack>
        </HStack>
      </Flex>
    </>
  )
}
AgentCard.defaultProps = {
  isMobile: false
}
export default AgentCard
